/**
 * Defines the available logging levels.
 * Hierarchy is implied by usage, though not strictly enforced by the logger itself:
 * error > warn > info > debug.
 */
import type { LoggerAdapter } from './adapters/index.js';
import { CompositeAdapter, EnvironmentAdapter, LocalStorageAdapter } from './adapters/index.js';
import type { LoggerStrategy } from './strategies/index.js';
import { ColoredConsoleStrategy } from './strategies/index.js';
import type { LogRule, LogLevel } from './types.js';

/**
 * A lightweight, optimized debugging utility modeled after the `debug` library.
 * Provides namespaced logging with wildcard support, color coding, and timestamp diffs.
 *
 * Key features:
 * - **Namespaces**: Hierarchical logging (e.g., `app:auth`, `app:db`).
 * - **Optimized**: Heavy operations (Date, Array.slice) only run if the logger is enabled.
 * - **Memory Safe**: Caches are capped to prevent leaks in long-running applications.
 *
 * @example
 * ```typescript
 * const debug = LoggerService.create('app:auth');
 * debug.log('user logged in', { userId: 123 });
 *
 * const debugDb = debug.extend('database');
 * debugDb.log('query executed');
 * ```
 *
 * Enable logging via localStorage:
 * - `localStorage.setItem('FLUX_CONTROL.DEBUG', '*')` - Enable all
 * - `localStorage.setItem('FLUX_CONTROL.DEBUG', 'app:*')` - Enable app namespace
 * - `localStorage.setItem('FLUX_CONTROL.DEBUG', '*,-app:auth')` - Enable all except app:auth
 * - `localStorage.setItem('FLUX_CONTROL.DEBUG', '*#error')` - Enable only error logs for all namespaces
 */
export class LoggerService<T extends string> {
  /**
   * The character used to separate namespace segments.
   */
  public static readonly NAMESPACE_SEPARATOR = ':' as const;

  /**
   * Max length of a namespace before it gets compacted.
   */
  public static readonly NAMESPACE_COMPACT_THRESHOLD = 60;

  /**
   * Maximum number of entries in the enabledCache before a flush occurs.
   */
  private static readonly CACHE_LIMIT = 1_000;

  private static readonly COLORS = [
    '#74b9ff',
    '#ff7675',
    '#55efc4',
    '#ffeaa7',
    '#a29bfe',
    '#81ecec',
    '#fab1a0',
    '#fd79a8',
    '#fdcb6e',
    '#00cec9',
    '#0984e3',
    '#6c5ce7',
    '#e17055',
    '#d63031',
    '#e84393',
  ];

  private static adapter: LoggerAdapter = new CompositeAdapter([
    new LocalStorageAdapter(),
    new EnvironmentAdapter(),
  ]);

  private static strategy: LoggerStrategy = new ColoredConsoleStrategy();

  // --- Internal State ---
  /**
   * Cache of the raw localStorage string to detect changes efficiently.
   */
  private static currentStorageString: string | null = null;

  /**
   * Parsed patterns for namespaces to EXCLUDE (start with -).
   */
  private static skips: LogRule[] = [];

  /**
   * Parsed patterns for namespaces to INCLUDE.
   */
  private static names: LogRule[] = [];

  /**
   * Memoization cache for enabled checks to avoid regex re-evaluation.
   */
  private static enabledCache = new Map<string, boolean>();

  private readonly namespace: string;

  private readonly color: string;

  private readonly displayName: string;

  private readonly keys: Record<LogLevel, string>;

  private lastLogTime = 0;

  /**
   * Private constructor to enforce use of `create`.
   */
  private constructor(namespace: T) {
    this.namespace = namespace;
    this.color = LoggerService.selectColor(namespace);
    // Pre-calculate the compact name once at instantiation
    this.displayName = LoggerService.compactName(namespace);

    this.keys = {
      info: `${namespace}:info`,
      debug: `${namespace}:debug`,
      warn: `${namespace}:warn`,
      error: `${namespace}:error`,
    };
  }

  /**
   * Creates a new logger instance with the given namespace.
   *
   * @param namespace - The namespace for this logger (e.g., 'app:auth')
   */
  public static create<T extends string>(namespace: T): LoggerService<T> {
    return new LoggerService(namespace);
  }

  /**
   * Checks if a given namespace is enabled based on the DEBUG pattern.
   *
   * @param namespace - The namespace to check.
   * @param level - The log level to check against.
   */
  public static isEnabled(namespace: string, level: LogLevel = 'info'): boolean {
    const key = `${namespace}:${level}`;
    const cached = LoggerService.enabledCache.get(key);
    if (cached !== undefined) return cached;
    return LoggerService.computeEnabled(namespace, level, key);
  }

  /**
   * Enables debugging for the given pattern.
   *
   * @param pattern - The pattern to enable (e.g., '*', 'app:*', '*,-app:auth')
   */
  public static enable(pattern: string): void {
    LoggerService.adapter.enable(pattern);
    LoggerService.clearCache();
  }

  /**
   * Disables all debugging.
   */
  public static disable(): void {
    LoggerService.adapter.disable();
    LoggerService.clearCache();
  }

  /**
   * Sets the adapter to be used by the logger.
   *
   * @param adapter - The adapter to set.
   */
  public static setAdapter(adapter: LoggerAdapter): void {
    LoggerService.adapter = adapter;
    LoggerService.clearCache();
  }

  /**
   * Sets the strategy to be used for formatting log output.
   *
   * @param strategy - The strategy to set.
   */
  public static setStrategy(strategy: LoggerStrategy): void {
    LoggerService.strategy = strategy;
  }

  /**
   * Gets the current DEBUG pattern.
   */
  public static getPattern(): string | null {
    return LoggerService.adapter.getPattern();
  }

  /**
   * Clears the internal caches. Useful when DEBUG setting changes.
   */
  public static clearCache(): void {
    LoggerService.enabledCache.clear();
    LoggerService.currentStorageString = null;
    LoggerService.skips = [];
    LoggerService.names = [];
  }

  /**
   * Extends the current logger with an additional namespace segment.
   *
   * @param namespace - The namespace segment to append
   */
  public extend<E extends string>(
    namespace: E,
  ): LoggerService<`${T}${(typeof LoggerService)['NAMESPACE_SEPARATOR']}${E}`> {
    return new LoggerService(`${this.namespace}${LoggerService.NAMESPACE_SEPARATOR}${namespace}`);
  }

  /**
   * Logs a message if this logger's namespace is enabled. Alias for `info`.
   */
  public log(...args: unknown[]): void {
    this.output('info', args);
  }

  /**
   * Logs a debug message if this logger's namespace is enabled.
   */
  public debug(...args: unknown[]): void {
    this.output('debug', args);
  }

  /**
   * Logs a warning message if this logger's namespace is enabled.
   */
  public warn(...args: unknown[]): void {
    this.output('warn', args);
  }

  /**
   * Logs an error message if this logger's namespace is enabled.
   */
  public error(...args: unknown[]): void {
    this.output('error', args);
  }

  /**
   * Logs an info message if this logger's namespace is enabled.
   */
  public info(...args: unknown[]): void {
    this.output('info', args);
  }

  /**
   * Checks if this logger instance is enabled based on the DEBUG pattern.
   *
   * @param level - Optional log level to check against. Defaults to 'info'.
   */
  public enabled(level: LogLevel = 'info'): boolean {
    const key = this.keys[level];
    const cached = LoggerService.enabledCache.get(key);
    if (cached !== undefined) return cached;

    return LoggerService.computeEnabled(this.namespace, level, key);
  }

  // --- Internal Implementation ---

  private output(level: LogLevel, args: unknown[]): void {
    if (!this.enabled(level)) return;

    const now = Date.now();
    const timeDiff = now - (this.lastLogTime || now);
    this.lastLogTime = now;

    LoggerService.strategy.log({
      args,
      color: this.color,
      displayName: this.displayName,
      level,
      namespace: this.namespace,
      timeDiff,
    });
  }

  private static computeEnabled(namespace: string, level: LogLevel, key: string): boolean {
    LoggerService.loadPatterns();

    for (const skip of LoggerService.skips) {
      if (skip.regex.test(namespace) && (!skip.level || skip.level === level)) {
        LoggerService.cacheResult(key, false);
        return false;
      }
    }

    for (const name of LoggerService.names) {
      if (name.regex.test(namespace) && (!name.level || name.level === level)) {
        LoggerService.cacheResult(key, true);
        return true;
      }
    }

    LoggerService.cacheResult(key, false);
    return false;
  }

  private static cacheResult(key: string, enabled: boolean): void {
    if (LoggerService.enabledCache.size > LoggerService.CACHE_LIMIT) {
      LoggerService.enabledCache.clear();
    }

    LoggerService.enabledCache.set(key, enabled);
  }

  private static loadPatterns(): void {
    const debugStr = LoggerService.adapter.getPattern() ?? '';

    if (debugStr === LoggerService.currentStorageString) return;

    LoggerService.currentStorageString = debugStr;
    LoggerService.skips = [];
    LoggerService.names = [];
    LoggerService.enabledCache.clear();

    if (!debugStr) return;

    const parts = debugStr.split(/[\s,]+/);

    for (const part of parts) {
      if (!part) continue;

      let pattern = part;
      let level: LogLevel | null = null;

      const hashIdx = pattern.indexOf('#');
      if (hashIdx !== -1) {
        level = pattern.slice(hashIdx + 1) as LogLevel;
        pattern = pattern.slice(0, hashIdx);
      }

      const isSkip = pattern.startsWith('-');
      if (isSkip) {
        pattern = pattern.slice(1);
      }

      // If pattern is empty but we have a level, it implies all namespaces
      if (!pattern && level) {
        pattern = '*';
      }

      if (!pattern) continue;

      // Escape regex characters except *
      const escapedPattern = pattern
        .replaceAll(/[$()+.?[\\\]^{|}]/g, '\\$&')
        .replaceAll('*', '.*?');
      const regex = new RegExp(`^${escapedPattern}$`);

      if (isSkip) {
        LoggerService.skips.push({
          regex,
          level,
        });
      } else {
        LoggerService.names.push({
          regex,
          level,
        });
      }
    }
  }

  private static selectColor(namespace: string): string {
    let hash = 0;
    for (let idx = 0; idx < namespace.length; idx++) {
      hash = (hash << 5) - hash + (namespace.codePointAt(idx) ?? 0);
      hash = Math.trunc(hash);
    }

    return LoggerService.COLORS[Math.abs(hash) % LoggerService.COLORS.length] ?? '#000000';
  }

  private static compactName(namespace: string): string {
    if (namespace.length <= LoggerService.NAMESPACE_COMPACT_THRESHOLD) return namespace;
    return namespace
      .split(LoggerService.NAMESPACE_SEPARATOR)
      .map((part, idx, arr) => (idx < arr.length - 1 ? part[0] : part))
      .join(LoggerService.NAMESPACE_SEPARATOR);
  }
}
